import React from 'react';
import { useNavigate } from 'react-router-dom';
import MonumentErrorLayout from './MonumentErrorLayout';
import { pickSafeRussianBackendMessage } from '../../utils/safeRussianBackendMessage';

/** bgImageName — только коды из manifest (401/403/404/429/500/503); status в UI может отличаться (напр. 428). */
const STATES = {
  COMPANY_REQUIRED: {
    title: 'Для продолжения нужна компания',
    status: '403',
    description: 'Чтобы работать с платформой, создайте свою компанию. Вступление в команду заказчика — только по приглашению HRM (не по публичному коду).',
    actionText: 'Создать компанию',
    actionLink: '/company/create',
    secondaryActionText: 'Связанные компании',
    secondaryActionLink: '/settings/connected-companies',
    bgImageName: '403'
  },
  VERIFICATION_REQUIRED: {
    title: 'Компания ещё не верифицирована',
    status: '403',
    description: 'Для этого действия нужно подтвердить компанию. Откройте настройки связанных компаний и загрузите необходимые документы.',
    actionText: 'Перейти к верификации',
    actionLink: '/settings?tab=connected_companies',
    bgImageName: '403'
  },
  COMPANY_NOT_VERIFIED: {
    title: 'Компания ещё не верифицирована',
    status: '403',
    description: 'Для этого действия нужно подтвердить компанию. Откройте настройки связанных компаний и загрузите необходимые документы.',
    actionText: 'Перейти к верификации',
    actionLink: '/settings?tab=connected_companies',
    bgImageName: '403'
  },
  MODULE_DISABLED: {
    title: 'Модуль сейчас недоступен',
    status: '503',
    description: 'Модуль отключён для вашей компании, не входит в тариф или временно недоступен. Проверьте тариф и настройки или обратитесь к администратору компании.',
    actionText: 'Тарифы и настройки',
    actionLink: '/settings?tab=tariffs',
    bgImageName: '503'
  },
  FEATURE_NOT_AVAILABLE: {
    title: 'Функция недоступна на текущем тарифе',
    status: '403',
    description: 'Эта возможность не входит в текущий тариф или отключена для компании. Посмотрите доступные тарифы или обратитесь в поддержку.',
    actionText: 'Посмотреть тарифы',
    actionLink: '/settings?tab=tariffs',
    bgImageName: '403'
  },
  COMPANY_CREATION_DISABLED: {
    title: 'Создание компаний сейчас отключено',
    status: '403',
    description: 'На площадке временно приостановлена регистрация новых компаний. Свяжитесь с администрацией платформы, чтобы уточнить детали.',
    actionText: 'Связаться с поддержкой',
    actionLink: '/contact',
    bgImageName: '403'
  },
  AGREEMENT_REQUIRED: {
    title: 'Нужно принять соглашение',
    status: '428',
    description: 'Для продолжения ознакомьтесь и подпишите актуальные соглашения в разделе «Документы и согласия». Это занимает не больше минуты.',
    actionText: 'Открыть документы и согласия',
    actionLink: '/settings?tab=consents',
    bgImageName: '403'
  },
  SECURITY_UNAVAILABLE: {
    title: 'Контур безопасности сейчас недоступен',
    status: '503',
    description: 'Проверка безопасности временно не отвечает. Попробуйте повторить действие через несколько минут. Если ошибка сохраняется — напишите в поддержку.',
    actionText: 'Написать в поддержку',
    actionLink: '/contact',
    bgImageName: '503'
  },
  PLATFORM_COMPANY_REQUIRED: {
    title: 'Нужен контекст ООО «МАРКБЭЙС»',
    status: '403',
    description: 'Платформенное управление (админка, LogCenter, настройки всей системы) доступно только от имени управляющей компании ООО «МАРКБЭЙС». ИП Мусинский — для торговли на площадке; переключите компанию в шапке.',
    actionText: 'Настройки компаний',
    actionLink: '/settings?tab=connected_companies',
    bgImageName: '403'
  },
  SYSTEM_MARKBASE_COMPANY_REQUIRED: {
    title: 'Нужен системный контур Markbase',
    status: '403',
    description: 'Карта конкурентов доступна в контурах ООО «МАРКБЭЙС» или ИП Мусинский (и компаниям из allowlist в настройках). Переключите компанию в шапке.',
    actionText: 'Настройки компаний',
    actionLink: '/settings?tab=connected_companies',
    bgImageName: '403'
  }
};

const BlockingState = ({ type = 'COMPANY_REQUIRED', customAction, serverMessage }) => {
  const navigate = useNavigate();
  const stateConfig = STATES[type] || STATES.COMPANY_REQUIRED;
  const description = (serverMessage && String(serverMessage).trim())
    ? pickSafeRussianBackendMessage(serverMessage, stateConfig.description)
    : stateConfig.description;

  const secondaryBtn = stateConfig.secondaryActionText ? (
    <>
      <button type="button" className="mb-err-page__cta" onClick={customAction || (() => navigate(stateConfig.actionLink))}>
        {stateConfig.actionText}
      </button>
      <button
        type="button"
        className="mb-err-page__cta mb-err-page__cta--secondary"
        onClick={() => navigate(stateConfig.secondaryActionLink)}
        style={{ marginTop: 'var(--s2, 8px)', opacity: 0.85 }}
      >
        {stateConfig.secondaryActionText}
      </button>
    </>
  ) : null;

  return (
    <MonumentErrorLayout
      status={stateConfig.status}
      title={stateConfig.title}
      description={description}
      actionText={stateConfig.actionText}
      actionLink={stateConfig.actionLink}
      bgImageName={stateConfig.bgImageName}
      customAction={customAction}
    >
      {secondaryBtn}
    </MonumentErrorLayout>
  );
};

export default BlockingState;
